import React from 'react';
import I18n from 'i18n-js';

import { TCITooltip } from '@/components/ui/Tooltip';
import AssignmentMadeTooltipContent from '@/components/common/AssignmentMadeTooltipContent';
import { GameInfo } from '../types';
import styles from './GameCard.module.scss';
import { HARDCODED_THUMBNAIL_URL } from './hardcodedThumbnails';
import { userInfo } from '../../actions';

import assignIcon from '../../../../../public/assets/images/assign.svg';
import assignedIcon from '../../../../../public/assets/images/assigned.svg';
import gradesIcon from '../../../../../public/assets/images/grades.svg';

interface Props {
  data: GameInfo;
  onAssignClick: (
    gameId: number,
    lessonId: number,
    initialValues: { modelType: string; modelsToAssign: string; }
  ) => void;
  onGradeClick: (lessonId: number) => void;
}

const GameCard = ({ data, onAssignClick, onGradeClick }: Props) => {
  const isStudent = !userInfo.staffer;

  const handleAssignClick = () => {
    onAssignClick(data.id, data.lesson_id, data.assignment_modal_initial_value);
  };

  const handleGradeClick = () => {
    onGradeClick(data.lesson_id);
  };

  const assignmentCreatedMessage = `${I18n.t('assignment_already_created')} ${I18n.t('go_to_assignments_to_review')}`;
  const assignButtonAriaLabel = `${I18n.t('create_assignment')}. ${data.assigned ? assignmentCreatedMessage : ''}`;

  const isStateCapitalsGame = data.name === 'State Capitals' || data.name === 'Juego de Capitales de Estado';
  const needHardcodedThumbnailImageUrl = Object.keys(HARDCODED_THUMBNAIL_URL).includes(data.name.toLowerCase());

  return (
    <div className={`card ${styles.gameCard}`}>
      <a
        aria-hidden
        className={
          styles.cardImg
          + (!needHardcodedThumbnailImageUrl && (!data.background_url || data.background_url === '/missing.png')
            ? ` ${styles.blackImg}`
            : '')
        }
        href={data.link}
        tabIndex={-1}
      >
        <img
          alt=""
          src={needHardcodedThumbnailImageUrl
            ? HARDCODED_THUMBNAIL_URL[data.name.toLowerCase() as keyof typeof HARDCODED_THUMBNAIL_URL]
            : data.background_url}
        />

        {data.question_count && (
          <div className={`${styles.questionCount} pos_bottom_right body sm`}>
            {I18n.t('libraries.games.question_count', { count: data.question_count })}
          </div>
        )}
      </a>

      <div className={styles.cardContent}>
        <div className={styles.titleBox}>
          <a className={`${styles.cardTitle} body tw-font-bold`} href={data.link}>
            {isStateCapitalsGame && <span className={styles.badge}>{I18n.t('libraries.games.new_badge')}&nbsp;</span>}
            {data.name}
          </a>
          <div className={`${styles.gameType} text labels sm`}>{data.game_type}</div>
        </div>

        <div className={styles.cardSubtitleWrapper}>
          <div className={`${styles.cardSubtitle} body sm`}>{data.unit_title}</div>
          <div className={`${styles.cardSubtitle} body sm`}>{data.lesson_title}</div>
        </div>
        {isStudent && data.assigned && (
          <div className={styles.actionBar}>
            <i
              aria-label={I18n.t('libraries.games.assignment_made_for_game')}
              className={`fa fa-calendar-check-o ${styles.assignedIcon}`}
              role="img"
            />
          </div>
        )}

        {!isStudent && (
          <div className={styles.actionBar}>
            <TCITooltip>
              <TCITooltip.Trigger asChild>
                <button aria-label={assignButtonAriaLabel} type="button" onClick={handleAssignClick}>
                  <img
                    alt=""
                    src={data.assigned ? assignedIcon : assignIcon}
                  />
                </button>
              </TCITooltip.Trigger>
              <TCITooltip.Content>
                {data.assigned ? <AssignmentMadeTooltipContent /> : I18n.t('create_assignment')}
              </TCITooltip.Content>
            </TCITooltip>
            <TCITooltip>
              <TCITooltip.Trigger asChild>
                <button aria-label={I18n.t('grade')} type="button" onClick={handleGradeClick}>
                  <img alt="" src={gradesIcon} />
                </button>
              </TCITooltip.Trigger>
              <TCITooltip.Content>
                {I18n.t('grade')}
              </TCITooltip.Content>
            </TCITooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;
